import { useEffect, useMemo, useState } from "react";

/** true when the person has asked for less motion (the global CSS rule also freezes animations; this lets scripts skip them) */
export const calm = (): boolean => (typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches) || (typeof document !== "undefined" && document.documentElement.classList.contains("reduce-motion"));

/**
 * Progress ring with content in the middle. The arc grows from empty to `value / max` once the ring has been drawn (a small
 * "filling up" moment) – with reduced motion it simply starts full.
 */
export function Ring({ value, max, size = 96, stroke = 10, tone = "blue", label, children }: { value: number; max: number; size?: number; stroke?: number; tone?: "blue" | "gold" | "green" | "pink"; label: string; children?: any }) {
  const pct = Math.max(0, Math.min(1, max ? value / max : 0));
  const [shown, setShown] = useState(calm());
  useEffect(() => {
    if (shown) return;
    const id = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
    return () => cancelAnimationFrame(id);
  }, [shown]);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const gid = useMemo(() => `ring-${tone}-${Math.round(size)}-${Math.round(pct * 100)}`, [tone, size, pct]);
  return (
    <div className={`fxring ${tone}`} style={{ width: size, height: size }} role="progressbar" aria-label={label} aria-valuemin={0} aria-valuemax={max} aria-valuenow={Math.min(value, max)}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} aria-hidden="true">
        <defs>
          <linearGradient id={gid} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={{ blue: "#38c8f5", gold: "#ffe066", green: "#7dff8a", pink: "#ff9ac0" }[tone]} />
            <stop offset="1" stopColor={{ blue: "#2f7bff", gold: "#ff9f1a", green: "#16a34a", pink: "#e0386f" }[tone]} />
          </linearGradient>
        </defs>
        <circle className="bg" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} />
        <circle className="fg" cx={size / 2} cy={size / 2} r={r} fill="none" strokeWidth={stroke} stroke={`url(#${gid})`} strokeLinecap="round" strokeDasharray={c} strokeDashoffset={shown ? c * (1 - pct) : c} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      </svg>
      <div className="mid">{children}</div>
    </div>
  );
}

const CONFETTI_COLORS = ["#ffc61a", "#ff5c8a", "#38c8f5", "#7dff8a", "#9b7bff", "#ff8a1f", "#ffffff"];

/** One short burst of confetti from the top of its (positioned) parent. Plays once, is decorative and is not rendered with reduced motion. */
export function Confetti({ n = 42, ms = 3200 }: { n?: number; ms?: number }) {
  const [on, setOn] = useState(!calm());
  useEffect(() => {
    if (!on) return;
    const id = setTimeout(() => setOn(false), ms);
    return () => clearTimeout(id);
  }, [on, ms]);
  const bits = useMemo(
    () => Array.from({ length: n }, (_, i) => ({ x: (i * 97) % 100, d: 0.9 + ((i * 53) % 18) / 10, delay: ((i * 31) % 12) / 20, rot: (i * 211) % 360, c: CONFETTI_COLORS[i % CONFETTI_COLORS.length]!, w: 7 + ((i * 7) % 6), drift: ((i * 37) % 80) - 40 })),
    [n],
  );
  if (!on) return null;
  return (
    <div className="confetti" aria-hidden="true">
      {bits.map((b, i) => (
        <i key={i} style={{ left: `${b.x}%`, background: b.c, width: b.w, height: b.w * 1.6, animationDuration: `${b.d + 1.4}s`, animationDelay: `${b.delay}s`, ["--rot" as any]: `${b.rot}deg`, ["--dx" as any]: `${b.drift}px` }} />
      ))}
    </div>
  );
}

/** Twinkling stars and rising sparkles – pure decoration for hero scenes. */
export function Sparkles({ n = 18 }: { n?: number }) {
  const dots = useMemo(() => Array.from({ length: n }, (_, i) => ({ x: (i * 37 + 11) % 100, y: (i * 53 + 7) % 70, s: 3 + ((i * 5) % 6), d: ((i * 17) % 30) / 10, t: 2.4 + ((i * 13) % 20) / 10 })), [n]);
  return (
    <div className="sparkles" aria-hidden="true">
      {dots.map((d, i) => (
        <i key={i} style={{ left: `${d.x}%`, top: `${d.y}%`, width: d.s, height: d.s, animationDelay: `${d.d}s`, animationDuration: `${d.t}s` }} />
      ))}
    </div>
  );
}

/* ═════════════ v12: 3D-effekter, der kan bruges overalt ═════════════ */

/**
 * One global pointer helper (installed once by the app shell) for three small effects – nothing to wire up on the pages:
 *  - `[data-tilt]`  tilts the element in 3D towards the mouse and moves a glare across it (mouse only; touch screens and "less motion" skip it)
 *  - `[data-spot]`  gets `--mx/--my` (a light that follows the pointer) and `--px/--py` (−1…1, used by the parallax layers of the scenes)
 */
export function installPointerFx(): () => void {
  if (typeof document === "undefined") return () => {};
  let tilted: HTMLElement | null = null;
  let spot: HTMLElement | null = null;
  let raf = 0;
  let last: PointerEvent | null = null;
  const reset = (el: HTMLElement) => {
    el.classList.remove("tilting");
    for (const k of ["--rx", "--ry", "--gx", "--gy", "--tx", "--ty"]) el.style.removeProperty(k);
  };
  const apply = () => {
    raf = 0;
    const e = last;
    if (!e || e.pointerType !== "mouse" || calm()) return;
    const t = e.target as Element | null;
    const el = (t?.closest?.("[data-tilt]") ?? null) as HTMLElement | null;
    if (tilted && tilted !== el) { reset(tilted); tilted = null; }
    if (el) {
      const r = el.getBoundingClientRect();
      const px = (e.clientX - r.left) / Math.max(1, r.width), py = (e.clientY - r.top) / Math.max(1, r.height);
      const max = Number(el.dataset.tilt) || 7;
      el.style.setProperty("--ry", `${((px - 0.5) * 2 * max).toFixed(2)}deg`);
      el.style.setProperty("--rx", `${(-(py - 0.5) * 2 * max).toFixed(2)}deg`);
      el.style.setProperty("--gx", `${(px * 100).toFixed(1)}%`);
      el.style.setProperty("--gy", `${(py * 100).toFixed(1)}%`);
      el.style.setProperty("--tx", ((px - 0.5) * 2).toFixed(3));
      el.style.setProperty("--ty", ((py - 0.5) * 2).toFixed(3));
      el.classList.add("tilting");
      tilted = el;
    }
    const host = (t?.closest?.("[data-spot]") ?? null) as HTMLElement | null;
    if (spot && spot !== host) { spot.style.removeProperty("--px"); spot.style.removeProperty("--py"); spot = null; }
    if (host) {
      const r = host.getBoundingClientRect();
      const px = (e.clientX - r.left) / Math.max(1, r.width), py = (e.clientY - r.top) / Math.max(1, r.height);
      host.style.setProperty("--mx", `${(px * 100).toFixed(1)}%`);
      host.style.setProperty("--my", `${(py * 100).toFixed(1)}%`);
      host.style.setProperty("--px", ((px - 0.5) * 2).toFixed(3));
      host.style.setProperty("--py", ((py - 0.5) * 2).toFixed(3));
      spot = host;
    }
  };
  const onMove = (e: PointerEvent) => { last = e; if (!raf) raf = requestAnimationFrame(apply); };
  const onOut = (e: PointerEvent) => {
    const to = e.relatedTarget as Node | null;
    if (tilted && !(to && tilted.contains(to))) { reset(tilted); tilted = null; }
    if (spot && !(to && spot.contains(to))) { spot.style.removeProperty("--px"); spot.style.removeProperty("--py"); spot = null; }
  };
  document.addEventListener("pointermove", onMove, { passive: true });
  document.addEventListener("pointerout", onOut, { passive: true });
  return () => {
    document.removeEventListener("pointermove", onMove);
    document.removeEventListener("pointerout", onOut);
    if (raf) cancelAnimationFrame(raf);
    if (tilted) reset(tilted);
  };
}

/**
 * Effect guard. The decorative things that never stop (floating 3D coins and cubes, twinkling sparkles, drifting clouds, shine) cost a lot on
 * slow computers – old PCs, school Chromebooks, or any browser without a graphics card. Shortly after the app has started the guard counts
 * the real frames; if the speed is clearly too low it puts the class `lowfx` on <html>, which switches the pure decoration off (the games and
 * all information are unaffected). The person can also choose: `localStorage["edu-fx"]` = "on" (never switch off) or "off" (always off);
 * no value = automatic. A verdict from the guard is remembered for the browser tab, and for a few days on the same computer.
 */
export type EffectMode = "auto" | "on" | "off";
const FX_KEY = "edu-fx", FX_LOW = "edu-fx-low", FX_DAYS = 3;
export function effectMode(): EffectMode {
  try { const v = localStorage.getItem(FX_KEY); return v === "on" || v === "off" ? v : "auto"; } catch { return "auto"; }
}
export function setEffectMode(mode: EffectMode) {
  try {
    if (mode === "auto") localStorage.removeItem(FX_KEY); else localStorage.setItem(FX_KEY, mode);
    localStorage.removeItem(FX_LOW); sessionStorage.removeItem(FX_LOW);
  } catch { /* storage blocked – the choice only lasts until the page is closed */ }
  window.dispatchEvent(new Event("edu-fx-change"));
}
export function installEffectGuard(): () => void {
  if (typeof document === "undefined") return () => {};
  const root = document.documentElement;
  let stop = false, raf = 0, timer = 0, dirty = false;
  const remembered = () => {
    try {
      if (sessionStorage.getItem(FX_LOW) === "1") return true;
      const at = Number(localStorage.getItem(FX_LOW) ?? 0);
      return at > 0 && Date.now() - at < FX_DAYS * 86400_000;
    } catch { return false; }
  };
  const remember = () => { try { sessionStorage.setItem(FX_LOW, "1"); localStorage.setItem(FX_LOW, String(Date.now())); } catch { /* */ } };
  const onVis = () => { dirty = true; }; // switching tabs freezes the frames – a window that saw that proves nothing
  /** counts the frames drawn in about 2 seconds; two slow windows in a row (under roughly 20 frames a second) switch the decoration off */
  const measure = (slowBefore: boolean) => {
    let frames = 0, t0 = 0;
    dirty = document.visibilityState !== "visible";
    const tick = (now: number) => {
      if (stop) return;
      if (dirty) { timer = window.setTimeout(() => measure(false), 3000); return; }
      if (!t0) t0 = now;
      frames++;
      if (now - t0 < 1900) { raf = requestAnimationFrame(tick); return; }
      if ((frames * 1000) / (now - t0) >= 20) return; // fast enough
      if (!slowBefore) { timer = window.setTimeout(() => measure(true), 200); return; }
      root.classList.add("lowfx");
      remember();
    };
    raf = requestAnimationFrame(tick);
  };
  const start = () => {
    clearTimeout(timer); cancelAnimationFrame(raf);
    const mode = effectMode();
    if (mode === "off") { root.classList.add("lowfx"); return; }
    root.classList.remove("lowfx");
    if (mode === "on" || calm()) return;
    if (remembered()) { root.classList.add("lowfx"); return; }
    timer = window.setTimeout(() => measure(false), 900);
  };
  document.addEventListener("visibilitychange", onVis);
  window.addEventListener("edu-fx-change", start);
  start();
  return () => {
    stop = true; clearTimeout(timer); cancelAnimationFrame(raf);
    document.removeEventListener("visibilitychange", onVis);
    window.removeEventListener("edu-fx-change", start);
  };
}

/**
 * Spinning coins, tumbling question cubes and stars made of real CSS 3D (layers in depth + `rotate3d`) – decoration for the hero scenes.
 * Pure CSS, no canvas; with reduced motion they stand still.
 */
export function Floaties({ kinds = ["coin", "cube", "star"], n = 7, seed = 1 }: { kinds?: ("coin" | "cube" | "star" | "gem")[]; n?: number; seed?: number }) {
  const items = useMemo(
    () => Array.from({ length: n }, (_, i) => {
      const k = kinds[i % kinds.length]!;
      const r = (m: number) => ((i * 97 + seed * 31 + m * 53) % 100) / 100;
      return { k, x: 3 + r(1) * 92, y: 6 + r(2) * 66, s: (k === "cube" ? 30 : 34) + Math.round(r(3) * 24), d: -r(4) * 8, t: 5 + r(5) * 5, b: 3.2 + r(6) * 3, tone: (i + seed) % 3 };
    }),
    [kinds, n, seed],
  );
  return (
    <div className="floaties" aria-hidden="true">
      {items.map((it, i) => (
        <span key={i} className={`fl ${it.k}`} style={{ left: `${it.x}%`, top: `${it.y}%`, ["--s" as any]: `${it.s}px`, ["--d" as any]: `${it.d}s`, ["--t" as any]: `${it.t}s`, ["--b" as any]: `${it.b}s`, ["--tone" as any]: it.tone }}>
          <span className="fl-in">
            {it.k === "coin" && Array.from({ length: 7 }, (_, z) => <i key={z} style={{ ["--k" as any]: z - 3 }} className={z === 6 ? "face" : z === 0 ? "face back" : ""}>{z === 6 ? "★" : ""}</i>)}
            {it.k === "cube" && ["f1", "f2", "f3", "f4", "f5", "f6"].map((f) => <i key={f} className={f}>?</i>)}
            {it.k === "star" && <i />}
            {it.k === "gem" && <><i className="g1" /><i className="g2" /></>}
          </span>
        </span>
      ))}
    </div>
  );
}

/**
 * A short shower of coins and stars from a point (an element or x/y on the screen) – for a purchase, a finished mission, a win.
 * Positive reward feedback only; skipped with reduced motion. Cleans itself up.
 */
export function coinShower(from: HTMLElement | { x: number; y: number } | null, n = 20) {
  if (typeof document === "undefined" || calm()) return;
  let x = window.innerWidth / 2, y = window.innerHeight * 0.55;
  if (from && "getBoundingClientRect" in from) { const r = from.getBoundingClientRect(); x = r.left + r.width / 2; y = r.top + r.height / 2; }
  else if (from) { x = from.x; y = from.y; }
  const box = document.createElement("div");
  box.className = "coinfx";
  box.setAttribute("aria-hidden", "true");
  document.body.appendChild(box);
  for (let i = 0; i < n; i++) {
    const el = document.createElement("i");
    el.className = i % 5 === 4 ? "star" : "coin";
    el.style.left = `${x}px`;
    el.style.top = `${y}px`;
    box.appendChild(el);
    const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.9, sp = 220 + Math.random() * 360;
    const dx = Math.cos(ang) * sp, dy = Math.sin(ang) * sp;
    el.animate(
      [
        { transform: "translate(-50%,-50%) scale(0.3) rotateY(0deg)", opacity: 1 },
        { transform: `translate(calc(-50% + ${dx * 0.7}px), calc(-50% + ${dy * 0.85}px)) scale(1) rotateY(540deg)`, opacity: 1, offset: 0.42 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy + 560}px)) scale(0.8) rotateY(1080deg)`, opacity: 0 },
      ],
      { duration: 1150 + Math.random() * 500, delay: i * 16, easing: "cubic-bezier(0.2, 0.7, 0.4, 1)", fill: "both" },
    );
  }
  setTimeout(() => box.remove(), 2400);
}

/**
 * v17: coins that fly from `from` (the reward you just earned) up to the coin counter in the top bar, one after the other – and the counter
 * gives a happy bump when the last one lands. Pure decoration (nothing happens with "less motion" and the number is always shown anyway).
 */
export function coinFlight(from: HTMLElement | null, n = 8) {
  if (typeof document === "undefined" || calm() || !from) return;
  const target = document.querySelector<HTMLElement>(".topright .chip.coin") ?? document.querySelector<HTMLElement>(".tabbar");
  if (!target) return;
  const a = from.getBoundingClientRect(), b = target.getBoundingClientRect();
  const x0 = a.left + a.width / 2, y0 = a.top + a.height / 2;
  const dx = b.left + b.width / 2 - x0, dy = b.top + b.height / 2 - y0;
  const box = document.createElement("div");
  box.className = "coinfx";
  box.setAttribute("aria-hidden", "true");
  document.body.appendChild(box);
  const count = Math.max(3, Math.min(14, n));
  let landed = 0;
  for (let i = 0; i < count; i++) {
    const el = document.createElement("i");
    el.className = "coin";
    el.style.left = `${x0}px`;
    el.style.top = `${y0}px`;
    box.appendChild(el);
    const side = (i % 2 ? 1 : -1) * (30 + (i * 23) % 70);
    const anim = el.animate(
      [
        { transform: "translate(-50%,-50%) scale(0.4) rotateY(0deg)", opacity: 0 },
        { transform: `translate(calc(-50% + ${side}px), calc(-50% - 70px)) scale(1.15) rotateY(360deg)`, opacity: 1, offset: 0.3 },
        { transform: `translate(calc(-50% + ${dx}px), calc(-50% + ${dy}px)) scale(0.55) rotateY(900deg)`, opacity: 0.95 },
      ],
      { duration: 900 + (i % 4) * 90, delay: 250 + i * 70, easing: "cubic-bezier(0.45, 0, 0.75, 0.4)", fill: "both" },
    );
    anim.onfinish = () => {
      el.remove();
      target.classList.remove("coinhit");
      void target.offsetWidth;
      target.classList.add("coinhit");
      if (++landed === count) setTimeout(() => { target.classList.remove("coinhit"); box.remove(); }, 450);
    };
  }
  setTimeout(() => box.remove(), 4200);
}

/** a small spinning 3D coin (the same CSS coin as in the floating things) – for the coin counter */
export function SpinCoin({ size = 30 }: { size?: number }) {
  return (
    <span className="fl coin spincoin" aria-hidden="true" style={{ ["--s" as any]: `${size}px`, ["--t" as any]: "3.2s", ["--b" as any]: "2.6s", ["--d" as any]: "0s" }}>
      <span className="fl-in">
        {Array.from({ length: 7 }, (_, z) => <i key={z} style={{ ["--k" as any]: z - 3 }} className={z === 6 ? "face" : z === 0 ? "face back" : ""}>{z === 6 ? "★" : ""}</i>)}
      </span>
    </span>
  );
}

/**
 * v21: scroll reveal. Anything with `data-reveal` fades and rises into place the first time it scrolls into view (`data-reveal="left" | "right" | "zoom"`
 * changes the direction, the CSS variable `--i` staggers neighbours). Nothing is hidden if this helper does not run (the hiding rule needs `html.reveal-on`),
 * and "less motion" shows everything at once. The attribute is removed again when the entrance is over, so it never gets in the way of hover effects.
 */
export function installReveal(): () => void {
  if (typeof document === "undefined" || typeof IntersectionObserver === "undefined") return () => {};
  const root = document.documentElement;
  const seen = new WeakSet<Element>();
  const io = new IntersectionObserver((entries) => {
    for (const e of entries) {
      if (!e.isIntersecting) continue;
      const el = e.target as HTMLElement;
      io.unobserve(el);
      el.setAttribute("data-in", "1"); // an attribute, not a class: React rewrites `className` whenever it re-renders the element
      window.setTimeout(() => { el.removeAttribute("data-reveal"); el.removeAttribute("data-in"); }, 1100 + (Number(el.style.getPropertyValue("--i")) || 0) * 70);
    }
  }, { rootMargin: "0px 0px -6% 0px", threshold: 0.05 });
  const watch = (el: Element) => { if (!seen.has(el)) { seen.add(el); io.observe(el); } };
  /** `data-stagger` on a container: every direct child enters one after the other */
  const stagger = (c: Element) => { if (seen.has(c)) return; seen.add(c); Array.from(c.children).forEach((k, i) => { if (!k.hasAttribute("data-reveal")) { k.setAttribute("data-reveal", ""); (k as HTMLElement).style.setProperty("--i", String(Math.min(i, 9))); } watch(k); }); };
  /** things on every page that should enter by themselves (the section headings and the cards that stand side by side) */
  const AUTO = ".sec-head, .grid.cols-2 > .card, .grid.cols-3 > .card";
  const auto = (el: Element) => {
    if (seen.has(el) || el.hasAttribute("data-reveal")) return;
    el.setAttribute("data-reveal", "");
    const sibs = el.parentElement ? Array.from(el.parentElement.children) : [];
    (el as HTMLElement).style.setProperty("--i", String(Math.min(sibs.indexOf(el), 9)));
  };
  const scan = (host: ParentNode) => {
    host.querySelectorAll?.("[data-stagger]").forEach(stagger);
    host.querySelectorAll?.(AUTO).forEach(auto);
    host.querySelectorAll?.("[data-reveal]").forEach(watch);
  };
  scan(document);
  const mo = new MutationObserver((list) => {
    for (const m of list) m.addedNodes.forEach((n) => { if (n.nodeType !== 1) return; const el = n as Element; if (el.hasAttribute("data-stagger")) stagger(el); if (el.matches(AUTO)) auto(el); if (el.hasAttribute("data-reveal")) watch(el); scan(el); });
  });
  mo.observe(document.getElementById("root") ?? document.body, { childList: true, subtree: true });
  root.classList.add("reveal-on");
  return () => { io.disconnect(); mo.disconnect(); root.classList.remove("reveal-on"); };
}

/** v21: the top bar gets a stronger shadow once the page has been scrolled (`html[data-scrolled="1"]`) */
export function installScrollFx(): () => void {
  if (typeof window === "undefined") return () => {};
  const root = document.documentElement;
  let raf = 0;
  const upd = () => { raf = 0; const v = window.scrollY > 6 ? "1" : "0"; if (root.dataset.scrolled !== v) root.dataset.scrolled = v; };
  const on = () => { if (!raf) raf = requestAnimationFrame(upd); };
  window.addEventListener("scroll", on, { passive: true });
  upd();
  return () => { window.removeEventListener("scroll", on); if (raf) cancelAnimationFrame(raf); delete root.dataset.scrolled; };
}
